import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, formatIDR, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import OrderRow from "@/components/OrderRow";
import WIBClock from "@/components/WIBClock";
import {
  ShoppingBag, Plane, Wallet, Clock, Plus, Package, MapPin, ArrowRight, Search,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [buying, setBuying] = useState([]);
  const [shipping, setShipping] = useState([]);
  const [activeTrips, setActiveTrips] = useState([]);
  const [myActiveTrips, setMyActiveTrips] = useState([]);
  const [openRequests, setOpenRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Iteration 11: Unified accounts → use active_mode, not legacy role.
  const activeMode = user?.active_mode || user?.role || "buyer";
  const isJastiper = activeMode === "jastiper";
  const isBuyer = activeMode === "buyer";

  useEffect(() => {
    const calls = [
      api.get("/stats/dashboard").then((r) => setStats(r.data)),
      api.get("/orders?role=buyer").then((r) => setBuying(r.data)),
      api.get("/orders?role=jastiper").then((r) => setShipping(r.data)),
    ];
    if (isBuyer) {
      calls.push(api.get("/trips").then((r) => setActiveTrips(r.data.slice(0, 6))));
    } else if (isJastiper) {
      calls.push(api.get("/requests").then((r) => setOpenRequests(r.data.slice(0, 6))));
      calls.push(
        api.get("/trips?mine=true&status=active").then((r) => setMyActiveTrips(r.data.slice(0, 6)))
      );
    }
    Promise.all(calls).finally(() => setLoading(false));
  }, [isBuyer, isJastiper]);

  if (loading) {
    return (
      <div className="text-center py-20 text-zinc-500" data-testid="dashboard-loading">
        {t("dash.loading")}
      </div>
    );
  }

  const modeLabel = isJastiper ? t("dash.mode_jastiper") : t("dash.mode_buyer");

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <div className="label-caps text-zinc-500">{t("dash.welcome")}</div>
            <WIBClock />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight" data-testid="dashboard-heading">
            {t("dash.hello")}, {user?.name?.split(" ")[0]}.
          </h1>
          <div className="text-sm text-zinc-500 mt-1" data-testid="dashboard-role-label">
            {t("dash.signed_in_as")}{" "}
            <span
              className="inline-flex items-center px-2 py-0.5 ml-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] rounded"
              style={{
                backgroundColor: isJastiper ? "#059669" : "#002FA7",
                color: "white",
              }}
              data-testid="dashboard-role-pill"
            >
              {modeLabel}
            </span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isJastiper && (
            <Link to="/trips/new" className="btn-primary-flat text-sm" data-testid="new-trip-btn">
              <Plus size={14} strokeWidth={1.5} /> {t("dash.new_trip")}
            </Link>
          )}
          {isBuyer && (
            <Link to="/requests/new" className="btn-primary-flat text-sm" data-testid="new-request-btn">
              <Plus size={14} strokeWidth={1.5} /> {t("dash.new_request")}
            </Link>
          )}
        </div>
      </div>

      {/* Stats Bento — role-aware */}
      {isJastiper ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l" style={{borderColor: "var(--app-border)"}}>
          <StatCard icon={Plane} label={t("dash.active_shipping")} value={stats?.shipping_active || 0} testid="stat-shipping" />
          <StatCard icon={Package} label={t("dash.active_trips")} value={stats?.active_trips || 0} testid="stat-active-trips" />
          <StatCard icon={Clock} label={t("dash.pending_earnings")} value={formatIDR(stats?.pending_earnings || 0)} testid="stat-pending" small />
          <StatCard icon={Wallet} label={t("dash.total_earned")} value={formatIDR(stats?.total_earnings || 0)} testid="stat-earned" small accent />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l" style={{borderColor: "var(--app-border)"}}>
          <StatCard icon={ShoppingBag} label={t("dash.active_buys")} value={stats?.buying_active || 0} testid="stat-buying" />
          <StatCard icon={ShoppingBag} label={t("dash.total_buys")} value={stats?.buying_count || 0} testid="stat-total-buys" />
          <StatCard icon={Clock} label={t("dash.open_requests")} value={stats?.open_requests || 0} testid="stat-open-requests" small />
          <StatCard icon={Search} label={t("dash.trips_available")} value={activeTrips.length || stats?.trips_available || 0} testid="stat-trips-available" small accent />
        </div>
      )}

      {/* Discovery */}
      {isBuyer && (
        <section data-testid="buyer-discovery-section">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="label-caps text-zinc-500 mb-1">{t("dash.discover")}</div>
              <h2 className="font-display text-2xl tracking-tight">{t("dash.discover_trips")}</h2>
              <p className="text-sm text-zinc-500 mt-1">{t("dash.discover_trips_desc")}</p>
            </div>
            <Link to="/trips" className="text-sm text-[#002FA7] hover:underline flex items-center gap-1" data-testid="view-all-trips-link">
              {t("dash.view_all")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
          {activeTrips.length === 0 ? (
            <div className="border py-12 text-center" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface)"}} data-testid="no-active-trips">
              <Plane size={32} strokeWidth={1.5} className="mx-auto mb-2" style={{color: "var(--app-muted)"}} />
              <p className="text-sm" style={{color: "var(--app-muted)"}}>{t("dash.no_active_trips")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-fade">
              {activeTrips.map((tr) => <TripDiscoveryCard key={tr.id} trip={tr} tr={t} />)}
            </div>
          )}
        </section>
      )}

      {isJastiper && (
        <section data-testid="jastiper-my-trips-section">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="label-caps text-zinc-500 mb-1">{t("dash.your_trips") || "Trip Anda"}</div>
              <h2 className="font-display text-2xl tracking-tight">{t("dash.my_active_trips") || "Trip Aktif Saya"}</h2>
              <p className="text-sm text-zinc-500 mt-1">{t("dash.my_active_trips_desc") || "Klik untuk lihat detail & kelola katalog item."}</p>
            </div>
            <Link to="/trips/new" className="text-sm hover:underline flex items-center gap-1" style={{color: "var(--app-brand)"}} data-testid="dash-new-trip-link">
              <Plus size={14} strokeWidth={1.5} /> {t("dash.new_trip")}
            </Link>
          </div>
          {myActiveTrips.length === 0 ? (
            <div className="border py-12 text-center" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface)"}} data-testid="jastiper-no-active-trips">
              <Plane size={32} strokeWidth={1.5} className="mx-auto mb-2" style={{color: "var(--app-muted)"}} />
              <p className="text-sm mb-3" style={{color: "var(--app-muted)"}}>{t("dash.no_my_trips") || "Belum ada trip aktif. Buat trip pertama Anda."}</p>
              <Link to="/trips/new" className="btn-primary-flat text-sm inline-flex" data-testid="empty-my-trips-create">
                <Plus size={14} strokeWidth={1.5} /> {t("dash.new_trip")}
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-fade">
              {myActiveTrips.map((tr) => <MyTripCard key={tr.id} trip={tr} tr={t} />)}
            </div>
          )}
        </section>
      )}

      {isJastiper && (
        <section data-testid="jastiper-discovery-section">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="label-caps text-zinc-500 mb-1">{t("dash.earn")}</div>
              <h2 className="font-display text-2xl tracking-tight">{t("dash.discover_requests")}</h2>
              <p className="text-sm text-zinc-500 mt-1">{t("dash.discover_requests_desc")}</p>
            </div>
            <Link to="/requests" className="text-sm text-[#002FA7] hover:underline flex items-center gap-1" data-testid="view-all-requests-link">
              {t("dash.view_all")} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
          {openRequests.length === 0 ? (
            <div className="border py-12 text-center" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface)"}} data-testid="no-open-requests">
              <ShoppingBag size={32} strokeWidth={1.5} className="mx-auto mb-2" style={{color: "var(--app-muted)"}} />
              <p className="text-sm" style={{color: "var(--app-muted)"}}>{t("dash.no_open_requests")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-fade">
              {openRequests.map((r) => <RequestDiscoveryCard key={r.id} request={r} tr={t} />)}
            </div>
          )}
        </section>
      )}

      {/* Orders Tabs */}
      <Tabs defaultValue={isJastiper ? "shipping" : "buying"} className="w-full" data-testid="dashboard-tabs">
        <TabsList className="bg-transparent border-b rounded-none w-full justify-start h-auto p-0 gap-0" style={{borderColor: "var(--app-border)"}}>
          {!isJastiper && (
            <TabsTrigger
              value="buying"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 font-medium"
              data-testid="tab-buying"
            >
              <ShoppingBag size={14} strokeWidth={1.5} className="mr-2" />
              {t("dash.my_orders")} ({buying.length})
            </TabsTrigger>
          )}
          {isJastiper && (
            <TabsTrigger
              value="shipping"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 font-medium"
              data-testid="tab-shipping"
            >
              <Plane size={14} strokeWidth={1.5} className="mr-2" />
              {t("dash.shipping")} ({shipping.length})
            </TabsTrigger>
          )}
        </TabsList>

        {!isJastiper && (
          <TabsContent value="buying" className="mt-8" data-testid="buying-tab-content">
            {buying.length === 0 ? (
              <EmptyState
                icon={ShoppingBag}
                title={t("dash.no_orders_title")}
                desc={t("dash.no_orders_desc")}
                actions={
                  <>
                    <Link to="/trips" className="btn-primary-flat text-sm" data-testid="empty-browse-trips">{t("dash.browse_trips")}</Link>
                    <Link to="/requests/new" className="btn-secondary-flat text-sm" data-testid="empty-post-request">{t("dash.post_request")}</Link>
                  </>
                }
              />
            ) : (
              <div className="border" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface)"}}>
                {buying.map((o) => <OrderRow key={o.id} order={o} role="buyer" />)}
              </div>
            )}
          </TabsContent>
        )}

        {isJastiper && (
          <TabsContent value="shipping" className="mt-8" data-testid="shipping-tab-content">
            {shipping.length === 0 ? (
              <EmptyState
                icon={Package}
                title={t("dash.no_shipments_title")}
                desc={t("dash.no_shipments_desc")}
                actions={
                  <>
                    <Link to="/trips/new" className="btn-primary-flat text-sm" data-testid="empty-new-trip">{t("dash.create_trip")}</Link>
                    <Link to="/requests" className="btn-secondary-flat text-sm" data-testid="empty-browse-requests">{t("dash.browse_requests")}</Link>
                  </>
                }
              />
            ) : (
              <div className="border" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface)"}}>
                {shipping.map((o) => <OrderRow key={o.id} order={o} role="jastiper" />)}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, small, accent }) {
  return (
    <div
      className="border-r border-b p-6"
      style={{
        borderColor: "var(--app-border)",
        backgroundColor: accent ? "var(--app-brand)" : "var(--app-surface)",
        color: accent ? "#FFFFFF" : "var(--app-fg)",
      }}
      data-testid={testid}
    >
      <Icon size={20} strokeWidth={1.5} className="mb-4" style={{ color: accent ? "rgba(255,255,255,0.85)" : "var(--app-brand)" }} />
      <div className="label-caps mb-2" style={{ color: accent ? "rgba(255,255,255,0.75)" : "var(--app-muted)" }}>{label}</div>
      <div className={`font-display ${small ? "text-xl" : "text-3xl"} tracking-tight`}>{value}</div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, desc, actions }) {
  return (
    <div className="border py-16 px-8 text-center" style={{ borderColor: "var(--app-border)", backgroundColor: "var(--app-surface)" }}>
      <Icon size={32} strokeWidth={1.5} className="mx-auto mb-4" style={{ color: "var(--app-muted)" }} />
      <div className="font-display text-xl mb-2">{title}</div>
      <p className="text-sm mb-6" style={{ color: "var(--app-muted)" }}>{desc}</p>
      <div className="flex gap-2 justify-center">{actions}</div>
    </div>
  );
}

function MyTripCard({ trip, tr }) {
  const itemCount = trip.catalog_items?.length || 0;
  const previewItem = trip.catalog_items?.find((i) => i.photo_path) || trip.catalog_items?.[0];
  return (
    <Link to={`/trips/${trip.id}`} className="grid-card group block" data-testid={`my-trip-card-${trip.id}`}>
      <div className="aspect-[16/10] overflow-hidden relative" style={{backgroundColor: "var(--app-surface-alt)"}}>
        {previewItem?.photo_path ? (
          <img src={fileUrl(previewItem.photo_path)} alt={previewItem.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{background: "linear-gradient(135deg, rgba(5,150,105,0.08), rgba(5,150,105,0.18))"}}>
            <Plane size={32} strokeWidth={1} style={{color: "var(--app-brand)", opacity: 0.5}} />
          </div>
        )}
        <div className="absolute top-2 left-2 bg-white/90 backdrop-blur px-2 py-0.5 label-caps">
          {itemCount} {itemCount === 1 ? tr("dash.item") : tr("dash.items")}
        </div>
        <div className="absolute top-2 right-2 px-2 py-0.5 label-caps text-white" style={{backgroundColor: "var(--app-brand)"}}>
          {tr("dash.trip_active_badge")}
        </div>
      </div>
      <div className="p-4 space-y-2">
        <div className="font-display text-base tracking-tight flex items-center gap-1.5 truncate">
          <MapPin size={12} strokeWidth={1.5} style={{color: "var(--app-brand)"}} className="flex-shrink-0" />
          {trip.destination_country}
          {trip.destination_city && <span className="text-xs font-sans font-normal" style={{color: "var(--app-muted)"}}>· {trip.destination_city}</span>}
        </div>
        <div className="font-mono text-xs border-t pt-2" style={{color: "var(--app-muted)", borderColor: "var(--app-border)"}}>
          {formatDate(trip.departure_date)} → {formatDate(trip.return_date)}
        </div>
        <div className="flex items-center justify-between pt-1 text-xs">
          <span style={{color: "var(--app-muted)"}}>{tr("dash.manage_catalog_arrow")}</span>
          <ArrowRight size={14} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" style={{color: "var(--app-brand)"}} />
        </div>
      </div>
    </Link>
  );
}

function TripDiscoveryCard({ trip, tr }) {
  const itemCount = trip.catalog_items?.length || 0;
  const previewItem = trip.catalog_items?.[0];
  return (
    <Link to={`/trips/${trip.id}`} className="grid-card group block" data-testid={`buyer-discovery-trip-${trip.id}`}>
      <div className="aspect-[16/10] bg-zinc-100 overflow-hidden relative">
        {previewItem?.photo_path ? (
          <img src={fileUrl(previewItem.photo_path)} alt={previewItem.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
            <Plane size={32} strokeWidth={1} className="text-[#002FA7]/40" />
          </div>
        )}
        <div className="absolute top-2 left-2 bg-white/90 backdrop-blur px-2 py-0.5 label-caps">
          {itemCount} {itemCount === 1 ? tr("dash.item") : tr("dash.items")}
        </div>
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display text-base tracking-tight flex items-center gap-1.5 truncate">
              <MapPin size={12} strokeWidth={1.5} className="text-[#002FA7] flex-shrink-0" />
              {trip.destination_country}
            </div>
            {trip.destination_city && <div className="text-xs text-zinc-500 truncate">{trip.destination_city}</div>}
          </div>
        </div>
        <div className="font-mono text-xs text-zinc-600 border-t border-zinc-100 pt-2">
          {formatDate(trip.departure_date)} → {formatDate(trip.return_date)}
        </div>
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 bg-[#002FA7] text-white flex items-center justify-center text-xs font-display flex-shrink-0">
              {trip.jastiper_name?.charAt(0).toUpperCase()}
            </div>
            <span className="text-xs truncate">{trip.jastiper_name}</span>
          </div>
          <ArrowRight size={14} strokeWidth={1.5} className="text-zinc-400 group-hover:text-[#002FA7] group-hover:translate-x-1 transition-all" />
        </div>
      </div>
    </Link>
  );
}

function RequestDiscoveryCard({ request, tr }) {
  const statusMap = {
    pending_price: { bg: "#FEF3C7", text: "#92400E", key: "req.status.pending_price" },
    priced: { bg: "#E0E7FF", text: "#3730A3", key: "req.status.priced" },
    agreed: { bg: "#DCFCE7", text: "#166534", key: "req.status.agreed" },
    declined: { bg: "#FEE2E2", text: "#991B1B", key: "req.status.declined" },
  };
  const s = statusMap[request.status] || statusMap.pending_price;
  return (
    <Link to={`/requests/${request.id}`} className="grid-card group block" data-testid={`jastiper-discovery-request-${request.id}`}>
      <div className="aspect-[16/10] bg-zinc-100 overflow-hidden relative">
        {request.photo_path ? (
          <img src={fileUrl(request.photo_path)} alt={request.item_name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-50 to-zinc-100">
            <ShoppingBag size={32} strokeWidth={1} className="text-zinc-300" />
          </div>
        )}
        <div className="absolute top-2 left-2 bg-white/90 backdrop-blur px-2 py-0.5 label-caps">
          {tr("dash.from")} {request.source_country}
        </div>
      </div>
      <div className="p-4 space-y-2">
        <div className="font-display text-base tracking-tight truncate">{request.item_name}</div>
        <p className="text-xs line-clamp-2" style={{color: "var(--app-muted)"}}>{request.description}</p>
        <div className="flex items-center justify-between pt-2 border-t" style={{borderColor: "var(--app-border)"}}>
          <div>
            <div className="text-[10px] uppercase tracking-wide" style={{color: "var(--app-muted)"}}>{tr("dash.offer")}</div>
            <div className="font-mono text-sm font-semibold" style={{color: "var(--app-brand)"}}>
              {request.quoted_price ? formatIDR(request.quoted_price) : "—"}
            </div>
          </div>
          <span
            className="label-caps px-2 py-1 font-mono text-[10px]"
            style={{ backgroundColor: s.bg, color: s.text }}
          >
            {tr(s.key)}
          </span>
        </div>
      </div>
    </Link>
  );
}
